import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, VStack, HStack, Box, Text, Badge, Spinner, Input, useToast, Divider,
} from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';
import { getSubmissionStatusColor, getSubmissionStatusLabel } from '../../utils/statusBadge';

const COLS =
  'id, patient_name, patient_dob, patient_mrn, patient_zip, patient_address, patient_street, patient_city, patient_state, status, submitted_at, lab_order_number, outcome_code, merged_into, ldx_message_id';

const lastNameOf = (name) => (name || '').trim().toLowerCase().split(/\s+/).pop() || '';
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { dateStyle: 'medium' }); } catch { return '—'; }
};
const fmtAddr = (s) =>
  s.patient_address || [s.patient_street, s.patient_city, s.patient_state, s.patient_zip].filter(Boolean).join(', ');

/**
 * Pick which submission to merge the current one with. Surfaces likely
 * duplicates (same last name / DOB / MRN), each shown with its date + status,
 * plus a manual "enter ID" fallback. Calls onPick(duplicateRow) when chosen.
 */
export default function MergeCandidatePicker({ isOpen, onClose, submission, onPick }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [manualId, setManualId] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (!isOpen || !submission?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const last = lastNameOf(submission.patient_name);
        const dob = submission.patient_dob || null;
        const mrn = submission.patient_mrn || null;

        const orParts = [];
        if (last) orParts.push(`patient_name.ilike.%${last}%`);
        if (dob) orParts.push(`patient_dob.eq.${dob}`);
        if (mrn) orParts.push(`patient_mrn.eq.${mrn}`);

        let query = supabase
          .from('submissions')
          .select(COLS)
          .neq('id', submission.id)
          .is('merged_into', null)
          .neq('status', 'cancelled')
          .order('submitted_at', { ascending: false })
          .limit(80);
        if (orParts.length) query = query.or(orParts.join(','));

        const { data, error } = await query;
        if (error) throw error;
        if (cancelled) return;

        // Rank: exact DOB + same last name first, then DOB, then name only.
        const ranked = (data || [])
          .map((c) => {
            const sameLast = last && lastNameOf(c.patient_name) === last;
            const sameDob = dob && c.patient_dob === dob;
            const sameMrn = mrn && c.patient_mrn === mrn;
            let score = 0;
            if (sameMrn) score += 4;
            if (sameDob) score += 2;
            if (sameLast) score += 1;
            return { ...c, _score: score };
          })
          .sort((a, b) => b._score - a._score);
        setCandidates(ranked);
      } catch (e) {
        toast({ title: 'Could not load duplicates', description: e.message, status: 'error', duration: 4000, isClosable: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, submission?.id, submission?.patient_name, submission?.patient_dob, submission?.patient_mrn, toast]);

  const pickById = async (rawId) => {
    const otherId = parseInt(String(rawId).replace(/[^0-9]/g, ''), 10);
    if (!otherId || otherId === submission.id) {
      toast({ title: 'Enter a different submission ID', status: 'warning', duration: 3000 });
      return;
    }
    setPicking(true);
    try {
      const { data, error } = await supabase.from('submissions').select('*').eq('id', otherId).maybeSingle();
      if (error || !data) {
        toast({ title: `Submission #${otherId} not found`, status: 'error', duration: 3000 });
        return;
      }
      onPick(data);
    } finally {
      setPicking(false);
    }
  };

  if (!submission) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Merge submission #{submission.id}
          <Text fontSize="sm" fontWeight="400" color="gray.500">
            Pick the duplicate to merge with {submission.patient_name || 'this patient'}. You'll confirm which record to keep next.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <HStack py={8} justify="center"><Spinner /><Text color="gray.500">Finding possible duplicates…</Text></HStack>
          ) : candidates.length === 0 ? (
            <Text color="gray.500" py={4}>No likely duplicates found. Use the manual entry below.</Text>
          ) : (
            <VStack align="stretch" spacing={2}>
              {candidates.map((c) => (
                <HStack
                  key={c.id}
                  justify="space-between"
                  p={3}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                  align="start"
                >
                  <Box minW={0}>
                    <HStack spacing={2} flexWrap="wrap">
                      <Text fontWeight="700" fontSize="sm">{c.patient_name || 'Unnamed'}</Text>
                      <Badge fontFamily="mono" fontSize="2xs">#{c.id}</Badge>
                      <Badge colorScheme={getSubmissionStatusColor(c.status)} fontSize="2xs">
                        {getSubmissionStatusLabel(c.status)}
                      </Badge>
                      {c.lab_order_number && (
                        <Badge colorScheme="blue" fontSize="2xs">Order #{c.lab_order_number}</Badge>
                      )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                      Submitted {fmtDate(c.submitted_at)}
                      {c.patient_dob ? ` · DOB ${fmtDate(c.patient_dob)}` : ''}
                    </Text>
                    {fmtAddr(c) && <Text fontSize="xs" color="gray.500" noOfLines={1}>{fmtAddr(c)}</Text>}
                  </Box>
                  <Button size="sm" colorScheme="purple" flexShrink={0} isLoading={picking} onClick={() => pickById(c.id)}>
                    Merge
                  </Button>
                </HStack>
              ))}
            </VStack>
          )}

          <Divider my={4} />
          <Text fontSize="sm" fontWeight="600" mb={2}>Or merge by submission ID</Text>
          <HStack>
            <Input
              size="sm"
              placeholder="e.g. 1670"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') pickById(manualId); }}
            />
            <Button size="sm" onClick={() => pickById(manualId)} isLoading={picking}>Find</Button>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
